if (!customElements.get('lyma-review-slider')) customElements.define('lyma-review-slider', class extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    const root = this;
    const slider = root.querySelector('[data-review-slider]');
    const track = slider?.querySelector('[data-review-track]');
    const dots = [...root.querySelectorAll('[data-review-dot]')];
    if (!slider || !track) return;

    const mobile = window.matchMedia('(max-width: 749px)');
    const cards = () => [...track.querySelectorAll('.review-card')].slice(0, 3);
    let activeIndex = Math.max(0, Number(slider.dataset.activeIndex || 0));
    const setActive = (index) => {
      activeIndex = index;
      slider.dataset.activeIndex = String(index);
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };
    const goToIndex = (requestedIndex) => {
      const visibleCards = cards();
      if (!visibleCards.length) return;
      const index = Math.max(0, Math.min(visibleCards.length - 1, requestedIndex));
      const first = visibleCards[0];
      const card = visibleCards[index];
      setActive(index);
      if (!mobile.matches) {
        track.style.removeProperty('transform');
        return;
      }
      const offset = card.offsetLeft - first.offsetLeft;
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      slider.dispatchEvent(new CustomEvent('lyma-review-change'));
    };

    setActive(activeIndex);
    if (mobile.matches) goToIndex(activeIndex);

    let touchStartX = 0;
    let touchStartY = 0;
    let touchActive = false;
    let touchHorizontal = false;
    const resetTouch = () => {
      touchActive = false;
      touchHorizontal = false;
    };
    const touchStart = (event) => {
      if (!mobile.matches || event.touches.length !== 1) return;
      const touch = event.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchActive = true;
      touchHorizontal = false;
    };
    const touchMove = (event) => {
      if (!touchActive || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      if (!touchHorizontal && Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
      if (Math.abs(deltaX) <= Math.abs(deltaY) + 4) return;
      touchHorizontal = true;
      event.preventDefault();
    };
    const touchEnd = (event) => {
      if (!touchActive) return;
      const touch = event.changedTouches?.[0];
      const deltaX = (touch?.clientX ?? touchStartX) - touchStartX;
      const deltaY = (touch?.clientY ?? touchStartY) - touchStartY;
      const horizontal = touchHorizontal || (Math.abs(deltaX) >= 16 && Math.abs(deltaX) > Math.abs(deltaY) + 4);
      resetTouch();
      if (!horizontal || Math.abs(deltaX) < 16) return;
      event.preventDefault();
      // Every gesture changes the index by exactly one, regardless of distance.
      goToIndex(activeIndex + (deltaX < 0 ? 1 : -1));
    };
    slider.addEventListener('touchstart', touchStart, { passive: true, signal });
    slider.addEventListener('touchmove', touchMove, { passive: false, signal });
    slider.addEventListener('touchend', touchEnd, { passive: false, signal });
    slider.addEventListener('touchcancel', resetTouch, { passive: true, signal });
    dots.forEach((dot) => dot.addEventListener('click', () => {
      goToIndex(Number(dot.dataset.reviewDot));
    }, { signal }));
    mobile.addEventListener('change', () => {
      if (mobile.matches) goToIndex(activeIndex);
      else track.style.removeProperty('transform');
      slider.dispatchEvent(new CustomEvent('lyma-review-change'));
    }, { signal });

    if (this.hasAttribute('auto-height')) this.enableAutoHeight(slider, track, signal);
  }

  enableAutoHeight(slider, track, signal) {
    const mobile = window.matchMedia('(max-width: 749px)');
    const update = () => {
      cancelAnimationFrame(this.heightFrame);
      this.heightFrame = requestAnimationFrame(() => {
        if (signal.aborted) return;
        if (!mobile.matches) {
          slider.style.removeProperty('height');
          return;
        }
        const cards = [...track.querySelectorAll('.review-card')].slice(0, 3);
        const active = cards[Math.max(0, Math.min(cards.length - 1, Number(slider.dataset.activeIndex || 0)))];
        if (!active) {
          slider.style.removeProperty('height');
          return;
        }
        const height = `${Math.ceil(active.getBoundingClientRect().height)}px`;
        if (slider.style.height !== height) slider.style.height = height;
      });
    };
    this.heightResizeObserver = new ResizeObserver(update);
    const observeCards = () => {
      this.heightResizeObserver.disconnect();
      track.querySelectorAll('.review-card').forEach(card => this.heightResizeObserver.observe(card));
      update();
    };
    this.heightMutationObserver = new MutationObserver(observeCards);
    this.heightMutationObserver.observe(track, { childList: true, subtree: true, characterData: true });
    slider.addEventListener('lyma-review-change', update, { signal });
    window.addEventListener('resize', update, { passive: true, signal });
    mobile.addEventListener('change', update, { signal });
    observeCards();
    if (document.fonts?.ready) document.fonts.ready.then(update).catch(() => {});
  }

  disconnectedCallback() {
    this.controller?.abort();
    cancelAnimationFrame(this.heightFrame);
    this.heightResizeObserver?.disconnect();
    this.heightMutationObserver?.disconnect();
  }
});
